// CsvUploadModal.tsx
import React, { useMemo, useState } from "react"

import {
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tooltip,
} from "@coval/ui"
import type { Cell, Row, Workbook } from "exceljs"
import { Download, Info, Upload } from "lucide-react"
import Papa from "papaparse"

import { logger } from "@/lib/logger"
import { cn } from "@/utils/cn"
import { formatBytes } from "@/utils/formatting/format-bytes"

import { TEST_SET_IMPORT_LIMITS } from "../constants"
import {
  getTestSetDisplayNameFromFileName,
  getTestSetDisplayNameValidationError,
  processCsvRowsToTestSetInput,
  type CsvUploadTestSetInput,
} from "./csv-upload-utils"

export type { CsvUploadTestSetInput } from "./csv-upload-utils"

const log = logger("CsvUploadModal")

interface ExcelJSWorkbookModule {
  Workbook: new () => Workbook
}

const loadExcelJS = async (): Promise<ExcelJSWorkbookModule> => {
  const { Workbook: ExcelJSWorkbook } = await import("exceljs")
  return { Workbook: ExcelJSWorkbook }
}

interface CsvUploadModalProps {
  onTestCasesProcessed: (testSetInput: CsvUploadTestSetInput) => Promise<void>
  variant?: "icon" | "text"
  triggerClassName?: string
}

const CsvUploadModal: React.FC<CsvUploadModalProps> = ({
  onTestCasesProcessed,
  variant = "icon",
  triggerClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const [datasetFile, setDatasetFile] = useState<File | null>(null)
  const [datasetName, setDatasetName] = useState<string>("")
  const [fileName, setFileName] = useState<string>("")
  const [previewData, setPreviewData] = useState<
    CsvUploadTestSetInput["testCases"] | null
  >(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [isUploading, setIsUploading] = useState(false)

  const previewInvalidCount = useMemo(
    () =>
      (previewData ?? []).filter(
        (tc) => !tc.input_str || tc.input_str.trim().length === 0,
      ).length,
    [previewData],
  )

  const EXAMPLE_CSV = `input,expected,customer_name,account_type
"Hello there","Greet the user",John,premium
"Check balance","Show balance",Jane,basic`

  const handleDownloadExample = () => {
    const blob = new Blob([EXAMPLE_CSV], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "test-cases-example.csv"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const resetFileSelection = () => {
    setDatasetFile(null)
    setDatasetName("")
    setFileName("")
    setPreviewData(null)
    setPreviewError(null)
  }

  const parseFileForPreview = async (file: File, displayName: string) => {
    const fileExtension = file.name.split(".").pop()?.toLowerCase()

    try {
      if (fileExtension === "csv") {
        return new Promise<void>((resolve) => {
          Papa.parse(file, {
            header: false,
            skipEmptyLines: true,
            complete: (results: Papa.ParseResult<string[]>) => {
              try {
                const testSetInput = processCsvRowsToTestSetInput(
                  results.data,
                  {
                    datasetName: displayName,
                    fileName: file.name,
                    skipEmptyInput: false,
                  },
                )
                setPreviewData(testSetInput.testCases)
                setPreviewError(null)
              } catch (error) {
                setPreviewError(
                  error instanceof Error
                    ? error.message
                    : "Failed to parse file",
                )
                setPreviewData(null)
              }
              resolve()
            },
            error: (error: Papa.ParseError) => {
              setPreviewError(`Error parsing CSV: ${error.message}`)
              setPreviewData(null)
              resolve()
            },
          })
        })
      } else if (fileExtension === "xlsx") {
        const reader = new FileReader()
        reader.onload = async (event: ProgressEvent<FileReader>) => {
          if (!event.target?.result) {
            setPreviewError("Error reading file: No data received")
            setPreviewData(null)
            return
          }
          const arrayBuffer = event.target.result as ArrayBuffer
          const ExcelJS = await loadExcelJS()
          const workbook = new ExcelJS.Workbook()
          await workbook.xlsx.load(arrayBuffer)
          const worksheet = workbook.worksheets[0]

          const jsonData: unknown[][] = []
          worksheet.eachRow((row: Row) => {
            const rowValues: unknown[] = []
            row.eachCell({ includeEmpty: true }, (cell: Cell) => {
              rowValues.push(cell.value)
            })
            jsonData.push(rowValues)
          })

          try {
            const testSetInput = processCsvRowsToTestSetInput(jsonData, {
              datasetName: displayName,
              fileName: file.name,
              skipEmptyInput: false,
            })
            setPreviewData(testSetInput.testCases)
            setPreviewError(null)
          } catch (error) {
            setPreviewError(
              error instanceof Error ? error.message : "Failed to parse file",
            )
            setPreviewData(null)
          }
        }

        reader.onerror = () => {
          setPreviewError("Error reading Excel file")
          setPreviewData(null)
        }

        reader.readAsArrayBuffer(file)
      }
    } catch {
      setPreviewError("Error processing file")
      setPreviewData(null)
    }
  }

  const handleSelectedFile = (file: File): boolean => {
    const extension = `.${file.name.split(".").pop()?.toLowerCase() || ""}`
    const isAllowedExtension = (
      TEST_SET_IMPORT_LIMITS.allowedExtensions as readonly string[]
    ).includes(extension)
    const displayNameError = getTestSetDisplayNameValidationError(file.name)
    if (
      !isAllowedExtension ||
      file.size > TEST_SET_IMPORT_LIMITS.maxFileSizeBytes ||
      displayNameError
    ) {
      const errorMessage = !isAllowedExtension
        ? `Unsupported file type. Accepted formats: ${TEST_SET_IMPORT_LIMITS.allowedExtensions.join(", ")}.`
        : file.size > TEST_SET_IMPORT_LIMITS.maxFileSizeBytes
          ? `File must be smaller than ${formatBytes(TEST_SET_IMPORT_LIMITS.maxFileSizeBytes)}.`
          : displayNameError
      setFileError(errorMessage)
      resetFileSelection()
      return false
    }

    const displayName = getTestSetDisplayNameFromFileName(file.name)
    setDatasetFile(file)
    setDatasetName(displayName)
    setFileName(file.name)
    setFileError(null)

    void parseFileForPreview(file, displayName)
    return true
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    if (!handleSelectedFile(file)) {
      event.target.value = ""
    }
  }

  const handleFileFromDrop = (file: File) => {
    handleSelectedFile(file)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFileFromDrop(file)
    }
  }

  const handleUploadToDatabase = async () => {
    if (!datasetFile || !datasetName) {
      alert("Must provide a file and name.")
      return false
    }
    if (fileError) {
      return false
    }
    const fileExtension = datasetFile.name.split(".").pop()?.toLowerCase()
    if (!fileExtension) {
      setFileError("File must have a valid extension.")
      return false
    }

    if (
      datasetFile.size > TEST_SET_IMPORT_LIMITS.maxFileSizeBytes ||
      !(TEST_SET_IMPORT_LIMITS.allowedExtensions as readonly string[]).includes(
        `.${fileExtension}`,
      )
    ) {
      setFileError(
        `Upload must be a ${TEST_SET_IMPORT_LIMITS.allowedExtensions.join(", ")} file smaller than ${formatBytes(TEST_SET_IMPORT_LIMITS.maxFileSizeBytes)}.`,
      )
      return false
    }

    try {
      if (fileExtension === "csv") {
        return new Promise<boolean>((resolve) => {
          Papa.parse(datasetFile, {
            header: false,
            skipEmptyLines: true,
            complete: (results: Papa.ParseResult<string[]>) => {
              void (async () => {
                try {
                  const testSetInput = processCsvRowsToTestSetInput(
                    results.data,
                    {
                      datasetName,
                      fileName,
                    },
                  )
                  await onTestCasesProcessed(testSetInput)
                  resolve(true)
                } catch (error) {
                  log.error("Error processing test cases", error)
                  alert(
                    error instanceof Error
                      ? error.message
                      : "Failed to process test cases. Please try again.",
                  )
                  resolve(false)
                }
              })()
            },
            error: (error: Papa.ParseError) => {
              log.error("Error parsing CSV", error)
              alert("Error parsing CSV. Please check your file and try again.")
              resolve(false)
            },
          })
        })
      } else if (fileExtension === "xlsx") {
        return new Promise<boolean>((resolve) => {
          const reader = new FileReader()
          reader.onload = async (event: ProgressEvent<FileReader>) => {
            if (!event.target?.result) {
              alert("Error reading file: No data received")
              resolve(false)
              return
            }
            const arrayBuffer = event.target.result as ArrayBuffer
            const ExcelJS = await loadExcelJS()
            const workbook = new ExcelJS.Workbook()
            await workbook.xlsx.load(arrayBuffer)
            const worksheet = workbook.worksheets[0]

            // Convert worksheet to array format (similar to xlsx output)
            const jsonData: unknown[][] = []
            worksheet.eachRow((row: Row) => {
              const rowValues: unknown[] = []
              row.eachCell({ includeEmpty: true }, (cell: Cell) => {
                rowValues.push(cell.value)
              })
              jsonData.push(rowValues)
            })

            try {
              const testSetInput = processCsvRowsToTestSetInput(jsonData, {
                datasetName,
                fileName,
              })
              await onTestCasesProcessed(testSetInput)
              resolve(true)
            } catch (error) {
              log.error("Error processing test cases", error)
              alert(
                error instanceof Error
                  ? error.message
                  : "Failed to process test cases. Please try again.",
              )
              resolve(false)
            }
          }

          reader.onerror = () => {
            log.error("Error reading file", reader.error)
            alert(
              "Error reading Excel file. Please check your file and try again.",
            )
            resolve(false)
          }

          reader.readAsArrayBuffer(datasetFile)
        })
      }
      alert("Unsupported file type. Please upload a CSV or Excel file.")
      return false
    } catch (error) {
      log.error("Error processing file", error)
      alert("Error processing file. Please try again.")
      return false
    }
  }

  return (
    <>
      {variant === "icon" ? (
        <Button
          variant="secondary"
          icon
          tooltip="Upload CSV or Excel file"
          onClick={() => setIsOpen(true)}
        >
          <Upload className="w-4 h-4" />
        </Button>
      ) : (
        <Card
          interactive
          className={cn("flex items-center gap-3 px-4 py-3", triggerClassName)}
          onClick={() => setIsOpen(true)}
        >
          <Upload className="w-4 h-4 text-coval-text-muted flex-shrink-0" />
          Import test cases from a file
        </Card>
      )}
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open && isUploading) {
            return
          }
          setIsOpen(open)
          if (!open) {
            setPreviewData(null)
            setPreviewError(null)
            setFileError(null)
            setDatasetFile(null)
            setFileName("")
            setDatasetName("")
          }
        }}
      >
        <DialogContent
          size="lg"
          onOpenAutoFocus={(e) => {
            e.preventDefault()
            ;(e.currentTarget as HTMLElement | null)?.focus()
          }}
        >
          <DialogHeader>
            <DialogTitle>Upload Test Cases</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="space-y-4">
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-medium text-coval-text-muted">
                  Expected File Format
                </p>
                <Tooltip
                  side="bottom"
                  align="start"
                  className="max-w-md bg-surface-0 border border-coval-border p-4"
                  content={
                    <>
                      <p className="text-sm text-coval-text-muted mb-3">
                        Upload a CSV or Excel file with test cases. The first
                        row should contain column headers.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <span className="inline-block bg-surface-1 text-coval-text-muted text-xs font-mono px-2 py-0.5 rounded mt-0.5">
                            input
                          </span>
                          <span className="text-sm text-coval-text-muted">
                            <span className="text-coval-danger-text">*</span>{" "}
                            The input scenario or prompt for the test case
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="inline-block bg-surface-1 text-coval-text-muted text-xs font-mono px-2 py-0.5 rounded mt-0.5">
                            expected
                          </span>
                          <span className="text-sm text-coval-text-muted">
                            (optional) The expected behavior or output
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="inline-block bg-surface-1 text-coval-text-muted text-xs font-mono px-2 py-0.5 rounded mt-0.5">
                            metadata
                          </span>
                          <span className="text-sm text-coval-text-muted">
                            (optional) JSON object with metadata (e.g.{" "}
                            <code className="text-xs bg-surface-1 px-1 rounded">
                              {`{"key": "value"}`}
                            </code>
                            )
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="inline-block bg-surface-1 text-coval-text-muted text-xs font-mono px-2 py-0.5 rounded mt-0.5">
                            [custom]
                          </span>
                          <span className="text-sm text-coval-text-muted">
                            (optional) Or use additional columns as metadata
                            keys
                          </span>
                        </div>
                      </div>
                      <div className="bg-surface-1 rounded-lg p-3 mt-4">
                        <p className="text-xs text-coval-text-muted font-medium mb-2">
                          Example:
                        </p>
                        {EXAMPLE_CSV.split("\n").map((line, index) => (
                          <code
                            key={index}
                            className="text-xs text-coval-text-muted block whitespace-pre font-mono overflow-x-auto"
                          >
                            {line}
                          </code>
                        ))}
                      </div>
                    </>
                  }
                >
                  <button type="button" aria-label="View expected file format">
                    <Info className="w-3.5 h-3.5 text-coval-text-muted" />
                  </button>
                </Tooltip>
                <Button
                  variant="ghost"
                  className="text-xs h-6 min-w-0 px-2 ml-auto"
                  onClick={handleDownloadExample}
                >
                  <Download className="w-3 h-3" />
                  Download example
                </Button>
              </div>
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`w-full border border-dashed rounded-lg p-6 text-center transition-colors ${
                  isDragging
                    ? "border-coval-border-active bg-surface-1"
                    : "border-coval-border bg-surface-0 hover:border-coval-border-active hover:bg-surface-1"
                }`}
              >
                <input
                  type="file"
                  id="footer-file-input"
                  accept=".csv, .xlsx"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <label
                  htmlFor="footer-file-input"
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  <Upload
                    className={`w-8 h-8 ${isDragging ? "text-coval-contrast" : "text-coval-text-muted"}`}
                  />
                  <div>
                    <p
                      className={`text-sm font-medium ${isDragging ? "text-coval-contrast" : "text-coval-text-muted"}`}
                    >
                      {isDragging
                        ? "Drop file here"
                        : "Click to upload or drag and drop"}
                    </p>
                    <p className="text-xs text-coval-text-muted mt-1">
                      CSV, XLSX (Max{" "}
                      {formatBytes(TEST_SET_IMPORT_LIMITS.maxFileSizeBytes)})
                    </p>
                  </div>
                </label>
              </div>

              {fileError && (
                <div
                  role="alert"
                  className="bg-coval-danger-surface border border-coval-danger-border rounded-lg p-3"
                >
                  <p className="text-sm text-coval-danger-on-surface">
                    {fileError}
                  </p>
                </div>
              )}

              {previewError && (
                <div className="bg-coval-danger-surface border border-coval-danger-border rounded-lg p-3">
                  <p className="text-sm text-coval-danger-on-surface">
                    {previewError}
                  </p>
                </div>
              )}

              {previewData && previewData.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-coval-text-muted mb-2">
                    Preview ({previewData.length} test case
                    {previewData.length !== 1 ? "s" : ""}
                    {previewInvalidCount > 0 && (
                      <>
                        {", "}
                        <span className="text-coval-danger-text">
                          {previewInvalidCount} invalid case
                          {previewInvalidCount !== 1 ? "s" : ""}
                        </span>
                      </>
                    )}
                    )
                  </p>
                  <div className="max-h-48 overflow-y-auto border border-coval-border rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-surface-1 sticky top-0">
                        <tr>
                          <th className="text-left p-2 font-medium text-coval-text-muted">
                            Input
                          </th>
                          <th className="text-left p-2 font-medium text-coval-text-muted">
                            Expected
                          </th>
                          <th className="text-left p-2 font-medium text-coval-text-muted">
                            Metadata
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.slice(0, 10).map((tc, idx) => {
                          const isMissingInput =
                            !tc.input_str || tc.input_str.trim().length === 0

                          return (
                            <tr
                              key={idx}
                              className={`border-t border-coval-border ${
                                isMissingInput ? "bg-coval-danger-surface" : ""
                              }`}
                            >
                              <td className="p-2 text-coval-text-muted max-w-[150px] truncate">
                                {tc.input_str || (
                                  <span className="text-coval-danger-on-surface font-medium">
                                    Missing required input
                                  </span>
                                )}
                              </td>
                              <td className="p-2 text-coval-text-muted max-w-[150px] truncate">
                                {tc.expected_behaviors?.join(", ") || "-"}
                              </td>
                              <td className="p-2 text-coval-text-muted max-w-[150px] truncate">
                                {tc.expected_output_json
                                  ? Object.entries(tc.expected_output_json)
                                      .map(
                                        ([k, v]) =>
                                          `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`,
                                      )
                                      .join(", ")
                                  : "-"}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                    {previewData.length > 10 && (
                      <div className="p-2 text-xs text-coval-text-muted text-center bg-surface-0">
                        ... and {previewData.length - 10} more
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setIsOpen(false)}
              disabled={isUploading}
            >
              Close
            </Button>
            <Button
              onClick={() => {
                if (isUploading) {
                  return
                }
                void (async () => {
                  setIsUploading(true)
                  try {
                    const success = await handleUploadToDatabase()
                    if (success) {
                      setIsOpen(false)
                    }
                  } finally {
                    setIsUploading(false)
                  }
                })()
              }}
              disabled={
                !datasetFile ||
                !datasetName ||
                Boolean(fileError) ||
                isUploading
              }
            >
              {isUploading ? "Uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default CsvUploadModal
